import type { Camera } from 'three';
import { Grid } from '../inventory/Grid';
import { distanceTo, type Xyz } from '../interact/Interact';
import type { PickupDirector } from '../interact/Pickups';
import { setPrompt } from '../ui/Prompt';
import { CC_SPAWN } from '../world/areas/a01/CrashCoast';

export const TORCH_WOOD = 1;
export const TORCH_FIBER = 1;
export const TORCH_LAMP_BOOST = 4.5;

export const DIG_WOOD = 2;
export const DIG_FIBER = 2;
export const DIG_RANGE = 3.2;
export const DIG_CENTER = { x: CC_SPAWN.x, z: CC_SPAWN.z };

export const CHOP_WOOD = 3;
export const CHOP_RANGE = 2.6;

/** Palm-near chop nodes. Inland of the beach path. */
export const PALM_CHOPS: { id: string; x: number; z: number }[] = [
  { id: 'CC-PALM-CHOP-01', x: 8, z: -21 },
  { id: 'CC-PALM-CHOP-02', x: 4, z: -34 },
  { id: 'CC-PALM-CHOP-03', x: -22, z: -52 },
];

export type TrackedDino = {
  taxon: string;
  x: number;
  y: number;
  z: number;
};

type ToolAction =
  | { kind: 'dig'; prompt: string }
  | { kind: 'chop'; id: string; prompt: string };

export function canCraftTorch(inv: { countOf(id: string): number }): boolean {
  return inv.countOf('torch') <= 0 && inv.countOf('wood') >= TORCH_WOOD && inv.countOf('fiber') >= TORCH_FIBER;
}

export function craftTorch(bag: { inventory: Grid; give(id: string, n?: number): void }): boolean {
  if (!canCraftTorch(bag.inventory)) {
    return false;
  }
  if (!bag.inventory.remove('wood', TORCH_WOOD) || !bag.inventory.remove('fiber', TORCH_FIBER)) {
    return false;
  }
  bag.give('torch');
  return true;
}

export function inDigSand(x: number, z: number): boolean {
  if (z > 14) {
    return false;
  }
  return Math.hypot(x - DIG_CENTER.x, z - DIG_CENTER.z) <= DIG_RANGE;
}

export function nearestPalm(player: Xyz): { id: string; x: number; z: number; dist: number } | null {
  let best: { id: string; x: number; z: number; dist: number } | null = null;
  for (const palm of PALM_CHOPS) {
    const dist = Math.hypot(player.x - palm.x, player.z - palm.z);
    if (dist > CHOP_RANGE) {
      continue;
    }
    if (!best || dist < best.dist) {
      best = { ...palm, dist };
    }
  }
  return best;
}

export function nearestTracked(
  player: Xyz,
  dinos: readonly TrackedDino[],
): { taxon: string; dist: number } | null {
  let best: { taxon: string; dist: number } | null = null;
  for (const dino of dinos) {
    const dist = distanceTo(player, dino);
    if (!best || dist < best.dist) {
      best = { taxon: dino.taxon, dist };
    }
  }
  return best;
}

export class ToolDirector {
  dug = false;
  readonly chopped = new Set<string>();
  prompt = '';

  constructor(private readonly pickups: PickupDirector) {}

  tick(camera: Camera, player: Xyz, held: string | null, use: boolean): boolean {
    void camera;
    const action = this.actionAt(player, held);
    this.prompt = action?.prompt ?? '';
    if (this.prompt) {
      setPrompt(this.prompt);
    }
    if (!use || !action) {
      return false;
    }
    return this.run(action);
  }

  forceUse(player: Xyz, held: string | null): boolean {
    const action = this.actionAt(player, held);
    return action ? this.run(action) : false;
  }

  syncPrompt(player: Xyz, held: string | null): void {
    const action = this.actionAt(player, held);
    this.prompt = action?.prompt ?? '';
    if (this.prompt) {
      setPrompt(this.prompt);
    }
  }

  private actionAt(player: Xyz, held: string | null): ToolAction | null {
    if (held === 'shovel' && !this.dug && inDigSand(player.x, player.z)) {
      return { kind: 'dig', prompt: 'E — Dig' };
    }
    if (held === 'axe' || held === 'metal_axe') {
      const palm = nearestPalm(player);
      if (palm && !this.chopped.has(palm.id)) {
        return { kind: 'chop', id: palm.id, prompt: 'E — Chop palm' };
      }
    }
    return null;
  }

  private run(action: ToolAction): boolean {
    if (action.kind === 'dig') {
      if (this.dug) {
        return false;
      }
      this.dug = true;
      this.pickups.give('wood', DIG_WOOD);
      this.pickups.give('fiber', DIG_FIBER);
      return true;
    }
    if (this.chopped.has(action.id)) {
      return false;
    }
    this.chopped.add(action.id);
    this.pickups.give('wood', CHOP_WOOD);
    return true;
  }
}
